/**
 * Bangladesh HS Code presets and duty rates.
 * Source: NBR Bangladesh Customs Tariff (illustrative, last reviewed 2026-05-10).
 * Always verify current rates with nbr.gov.bd before commercial use.
 */

export type Bilingual = { en: string; bn: string };

export interface HsCodePreset {
  hsCode: string;
  description: Bilingual;
  category: string;
  /** Customs Duty % */
  cd: number;
  /** Regulatory Duty % */
  rd: number;
  /** Supplementary Duty % */
  sd: number;
  /** VAT % (almost always 15) */
  vat: number;
  /** Advance Income Tax % */
  ait: number;
  /** Advance Tax % */
  at: number;
  /** If true, AIT is computed on Assessable Value only (not on cumulative base). */
  aitOnAv?: boolean;
  /** If true, AT is computed on Assessable Value only. */
  atOnAv?: boolean;
  notes?: Bilingual;
}

export const BD_HS_PRESETS: HsCodePreset[] = [
  {
    hsCode: "8517.13.00",
    description: { en: "Smartphone (mobile phone)", bn: "স্মার্টফোন (মোবাইল)" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 0,
    notes: {
      en: "Local assembly enjoys lower duty; CKD/SKD imports differ.",
      bn: "স্থানীয় সংযোজনে কম শুল্ক; CKD/SKD আমদানিতে ভিন্ন।",
    },
  },
  {
    hsCode: "8517.12.00",
    description: { en: "Feature phone", bn: "ফিচার ফোন" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 0,
  },
  {
    hsCode: "8517.71.00",
    description: { en: "Mobile phone accessories", bn: "মোবাইল ফোন অ্যাক্সেসরিজ" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8471.30.00",
    description: { en: "Laptop / Notebook computer", bn: "ল্যাপটপ / নোটবুক" },
    category: "electronics",
    cd: 5, rd: 0, sd: 0, vat: 0, ait: 5, at: 0, aitOnAv: true,
    notes: {
      en: "Laptops are largely VAT-exempt; AIT typically applies on AV.",
      bn: "ল্যাপটপ মূলত ভ্যাট অব্যাহতিপ্রাপ্ত; AIT সাধারণত মূল্যের উপর।",
    },
  },
  {
    hsCode: "8471.41.00",
    description: { en: "Desktop computer", bn: "ডেস্কটপ কম্পিউটার" },
    category: "electronics",
    cd: 5, rd: 0, sd: 0, vat: 5, ait: 5, at: 0,
  },
  {
    hsCode: "8703.23.30",
    description: { en: "Car (1500cc–2000cc, sedan)", bn: "গাড়ি (১৫০০-২০০০ সিসি)" },
    category: "vehicle",
    cd: 25, rd: 0, sd: 100, vat: 15, ait: 5, at: 5,
    notes: {
      en: "Engine capacity heavily influences SD: higher CC = higher SD bracket.",
      bn: "ইঞ্জিন সিসি অনুযায়ী সম্পূরক শুল্ক বাড়ে: বেশি সিসি = বেশি SD।",
    },
  },
  {
    hsCode: "8703.22.20",
    description: { en: "Car (1000cc–1500cc, small)", bn: "গাড়ি (১০০০-১৫০০ সিসি)" },
    category: "vehicle",
    cd: 25, rd: 0, sd: 45, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8703.24.30",
    description: { en: "Car (above 3000cc, premium)", bn: "গাড়ি (৩০০০+ সিসি)" },
    category: "vehicle",
    cd: 25, rd: 0, sd: 500, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8711.20.00",
    description: { en: "Motorcycle (50cc–250cc)", bn: "মোটরসাইকেল (৫০-২৫০ সিসি)" },
    category: "vehicle",
    cd: 25, rd: 0, sd: 20, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8528.72.00",
    description: { en: "TV (LED, above 32\")", bn: "টিভি (LED, ৩২\"+)" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "3304.99.00",
    description: { en: "Branded cosmetics / make-up", bn: "ব্র্যান্ডেড কসমেটিকস" },
    category: "consumer",
    cd: 25, rd: 3, sd: 20, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "6203.42.00",
    description: { en: "Clothing — ready-made (men's trousers)", bn: "তৈরি পোশাক (পুরুষের ট্রাউজার)" },
    category: "consumer",
    cd: 25, rd: 3, sd: 20, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8507.60.00",
    description: { en: "Power bank / Li-ion battery pack", bn: "পাওয়ার ব্যাংক / লি-আয়ন ব্যাটারি" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "8806.10.00",
    description: { en: "Drone / UAV (consumer)", bn: "ড্রোন / UAV" },
    category: "electronics",
    cd: 25, rd: 3, sd: 20, vat: 15, ait: 5, at: 5,
    notes: {
      en: "Restricted item — additional clearance from BTRC required.",
      bn: "নিষিদ্ধ/সীমিত পণ্য — BTRC থেকে অতিরিক্ত অনুমতি প্রয়োজন।",
    },
  },
  {
    hsCode: "8504.40.00",
    description: { en: "General electronics / power adapter", bn: "সাধারণ ইলেকট্রনিক্স / অ্যাডাপ্টার" },
    category: "electronics",
    cd: 10, rd: 0, sd: 0, vat: 15, ait: 5, at: 0,
  },
  {
    hsCode: "9504.50.00",
    description: { en: "Video game console", bn: "ভিডিও গেম কনসোল" },
    category: "electronics",
    cd: 25, rd: 3, sd: 0, vat: 15, ait: 5, at: 5,
  },
  {
    hsCode: "9101.11.00",
    description: { en: "Wrist watch (precious metal)", bn: "হাতঘড়ি (মূল্যবান ধাতু)" },
    category: "consumer",
    cd: 25, rd: 3, sd: 30, vat: 15, ait: 5, at: 5,
  },
];

export const BD_FTA_COUNTRIES: { code: string; name: Bilingual; pact: string; cdReductionPct: number }[] = [
  { code: "IN", name: { en: "India", bn: "ভারত" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "PK", name: { en: "Pakistan", bn: "পাকিস্তান" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "LK", name: { en: "Sri Lanka", bn: "শ্রীলঙ্কা" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "BT", name: { en: "Bhutan", bn: "ভুটান" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "NP", name: { en: "Nepal", bn: "নেপাল" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "MV", name: { en: "Maldives", bn: "মালদ্বীপ" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "AF", name: { en: "Afghanistan", bn: "আফগানিস্তান" }, pact: "SAFTA", cdReductionPct: 100 },
  { code: "MM", name: { en: "Myanmar", bn: "মিয়ানমার" }, pact: "BIMSTEC", cdReductionPct: 0 },
  { code: "TH", name: { en: "Thailand", bn: "থাইল্যান্ড" }, pact: "BIMSTEC", cdReductionPct: 0 },
  { code: "CN", name: { en: "China", bn: "চীন" }, pact: "APTA", cdReductionPct: 50 },
  { code: "KR", name: { en: "South Korea", bn: "দক্ষিণ কোরিয়া" }, pact: "APTA", cdReductionPct: 50 },
  { code: "JP", name: { en: "Japan", bn: "জাপান" }, pact: "None / MFN", cdReductionPct: 0 },
  { code: "US", name: { en: "United States", bn: "যুক্তরাষ্ট্র" }, pact: "None / MFN", cdReductionPct: 0 },
  { code: "GB", name: { en: "United Kingdom", bn: "যুক্তরাজ্য" }, pact: "None / MFN", cdReductionPct: 0 },
  { code: "DE", name: { en: "Germany", bn: "জার্মানি" }, pact: "None / MFN", cdReductionPct: 0 },
  { code: "OTHER", name: { en: "Other / MFN", bn: "অন্যান্য / MFN" }, pact: "MFN", cdReductionPct: 0 },
];

export const DUTY_LAST_UPDATED = "2026-05-10";
export const NBR_CUSTOMS_URL = "https://nbr.gov.bd/";
export const HS_CODE_FINDER_URL = "https://bangladeshcustoms.gov.bd/trade_info/tariff_schedule";
